// @flow
/* eslint-disable react/no-danger */
import * as React from 'react';
import classNames from 'classnames';
import _ from 'lodash';
import { APP_CONFIG } from 'common/constants';
import { MAKO_ANALYTICS_PAGE_IDS, IS_DEVEL } from 'client/constants';
import { Responsive, RabbiCredit, MakoAnalytics, Embed, BottomLink } from 'client/components/common';
import { type EnhancedProps, withTransition } from 'client/components/common/HOCs/with-transition';
import { fileLink } from 'common/utils/file-link';
import { analytics } from 'common/utils/ga';

import PhoneLogin from './PhoneLogin';

import * as css from './Intro.scss';

type Props = {|
	...EnhancedProps,
	className?: string,
	in: boolean,
	checklistInfo: ChecklistsItem,
	onCampaignStart: Function,
	parentURL: string,
	location: RouterLocation,
	userInfo: User,
	showAlreadyVotesPopup: Function,
|};

class Intro extends React.PureComponent<Props> {
	transitionCompleteTimeout: TimeoutID;

	static defaultProps = {
		className: '',
	};

	async componentDidMount() {
		const { onTransitionComplete, checklistInfo } = this.props;
		this.transitionCompleteTimeout = setTimeout(onTransitionComplete, parseInt(css.transitionIn, 10));
		const isHideIntro = _.get(checklistInfo, 'general.isHideMainPage', false);
		// tracker.init('UA-113524208-35');
		// if (!isHideIntro) {
		// 	ga.pageView('homepage');
		// }
		// if (idGA) {
		// tracker.init(idGA);
		if (!isHideIntro) {
			if (!IS_DEVEL) {
				// const location = `${APP_CONFIG.domains.client.hostname}${this.props.location.pathname}`;
				const location = `${APP_CONFIG.domains.client.hostname}/homepage`;

				analytics.gtag.event('page_view', {
					page_title: 'homepage',
					page_location: `${location}`,
					page_path: '/homepage',
				});
			}
		}
		// }
	}

	componentDidUpdate(prevProps: Props) {
		const { checklistInfo, showAlreadyVotesPopup } = this.props;
		const isWithRegistration = _.get(checklistInfo, 'general.isWithRegistration', false);
		const checklistId = _.get(checklistInfo, 'id', '');
		if (this.props.in !== prevProps.in) {
			const { onTransitionComplete } = this.props;
			let duration = parseInt(this.props.in ? css.transitionIn : css.transitionOut, 10);

			if (!this.props.in) {
				duration = 0;
			}
			clearTimeout(this.transitionCompleteTimeout);
			this.transitionCompleteTimeout = setTimeout(onTransitionComplete, duration);
		}

		// User loged in and he/she didnt vote before
		if (isWithRegistration && this.props.userInfo.uid !== prevProps.userInfo.uid && !this.props.userInfo.isVoted) {
			this.props.onCampaignStart();
			localStorage.setItem('current-checklist-withregistration', `${checklistId}`);
		}

		// User loged in and he/she voted before
		if (isWithRegistration && this.props.userInfo.uid !== prevProps.userInfo.uid && this.props.userInfo.isVoted) {
			localStorage.setItem(`voted-checklist-with-registration-${checklistId}`, `true`);
			showAlreadyVotesPopup();
		}
	}

	onStartButtonClick = (e: SyntheticEvent<HTMLButtonElement>) => {
		const { location } = this.props;
		analytics.gtag.event('start');
		if (location.pathname === '/eretznehederet') {
			if (window) {
				window.open('https://list.mako.co.il/eretznehederetvideo', '_self');
			}
		} else {
			this.props.onCampaignStart();
		}
	};

	render() {
		const { className, transitionState, checklistInfo, parentURL, location } = this.props;

		if (_.isEmpty(checklistInfo)) return null;

		const checklistId = _.get(checklistInfo, 'id', '');
		const desktopBgImage = _.get(checklistInfo, 'graphics.mainBackground', {});
		const mobileBgImage = _.get(checklistInfo, 'graphics.mainBackgroundMobile', {});
		const activityLogo = _.get(checklistInfo, 'main.checklistLogo', {});

		const desktopBgVideo = _.get(checklistInfo, 'graphics.mainBackgroundVideo', false);
		const mobileBgVideo = _.get(checklistInfo, 'graphics.mainBackgroundMobileVideo', false);

		const titleText = _.get(checklistInfo, 'main.mainTitle', '');
		const subtitleText = _.get(checklistInfo, 'main.mainSubTitle', '');
		const startButtonText = _.get(checklistInfo, 'main.startButtonText', '');

		const titleColor = _.get(checklistInfo, 'graphics.mainTitleColor', '#fff');
		const subTitleColor = _.get(checklistInfo, 'graphics.mainSubTitleColor', '#fff');

		const startButtonBg = _.get(checklistInfo, 'graphics.buttonsBackgroundColor', '#000');
		const startButtonTextColor = _.get(checklistInfo, 'graphics.buttonsTextColor', '#fff');

		const cooladataChannelId = _.get(checklistInfo, 'general.cooladataChannelID', '');
		const cooladataId = _.get(checklistInfo, 'general.cooladataID', '');

		const embedCode = decodeURIComponent(_.get(checklistInfo, 'general.pixelEntrance', ''));

		const isWithVideoBg = !!(desktopBgVideo || mobileBgVideo);
		const isWithRegistration = _.get(checklistInfo, 'general.isWithRegistration', false);
		const isSpecialChecklist = location.pathname === '/eretznehederet';
		return (
			<div className={classNames(css.intro, css[transitionState], className, isWithVideoBg && css.withVideoBg)}>
				<Embed code={embedCode} />
				{Responsive.isMatching(Responsive.NO_MOBILE) && !_.isEmpty(desktopBgImage) && !desktopBgVideo && (
					<div className={css.bgDesktop} style={{ backgroundImage: `url('${fileLink(desktopBgImage)}')` }} />
				)}
				{Responsive.isMatching(Responsive.MOBILE) && !_.isEmpty(mobileBgImage) && !mobileBgVideo && (
					<div className={css.bgMobile} style={{ backgroundImage: `url('${fileLink(mobileBgImage)}')` }} />
				)}
				{Responsive.isMatching(Responsive.NO_MOBILE) && desktopBgVideo && (
					<div className={css.videoBg}>
						<video muted autoPlay loop playsInline src={desktopBgVideo} className={css.videoDesktop} />
					</div>
				)}
				{Responsive.isMatching(Responsive.MOBILE) && mobileBgVideo && (
					<div className={css.videoBgMobile}>
						<video muted autoPlay loop playsInline src={mobileBgVideo} className={css.videoMobile} />
					</div>
				)}
				<div className={css.container}>
					{!_.isEmpty(activityLogo) && (
						<img src={fileLink(activityLogo)} alt="" className={css.campaignLogo} />
					)}

					<div className={css.campaignDescription}>
						{titleText && (
							<div className={css.title} style={{ color: titleColor }}>
								{titleText}
							</div>
						)}
						{subtitleText && (
							<div className={css.subTitle} style={{ color: subTitleColor }}>
								{subtitleText}
							</div>
						)}
					</div>
					{isWithRegistration && (
						<PhoneLogin
							withOnMainVideo={isWithVideoBg}
							checklistId={checklistId}
							titleColor={titleColor}
							btnBgColor={startButtonBg}
							btnTextColor={startButtonTextColor}
						/>
					)}
					{!isWithRegistration && (
						<button
							className={css.startBtn}
							style={{
								color: startButtonTextColor,
								backgroundColor: startButtonBg,
							}}
							type="button"
							onClick={this.onStartButtonClick}
						>
							<p>{startButtonText}</p>
						</button>
					)}
				</div>

				<div className={css.rabbiCredit} dir="ltr">
					<RabbiCredit theme={isSpecialChecklist ? 'light' : 'dark'} location={location} />
				</div>
				<BottomLink />
				<MakoAnalytics
					pageId={MAKO_ANALYTICS_PAGE_IDS.MAIN}
					channelId={cooladataChannelId}
					cooladataId={cooladataId}
					projectURL={parentURL}
				/>
			</div>
		);
	}
}

export default withTransition(Intro);
